import { useState, useCallback, useEffect } from 'react';

const APP_VERSION_KEY = 'app_version';
const UPDATE_SHOWN_KEY = 'update_shown';
const PENDING_UPDATE_VERSION_KEY = 'pending_update_version';

interface AppUpdateResult {
  isUpdateAvailable: boolean;
  triggerUpdate: () => Promise<void>;
  closeUpdatePopup: () => void;
  checkForUpdates: () => void;
  currentVersion: string | null;
}

export const useAppUpdate = (): AppUpdateResult => {
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const [currentVersion, setCurrentVersion] = useState<string | null>(localStorage.getItem(APP_VERSION_KEY));
  const [latestVersion, setLatestVersion] = useState<string | null>(null);

  const isLocalhost = Boolean(
    window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
  );

  const clearAppCaches = useCallback(async () => {
    if (!('caches' in window)) return;

    try {
      const cacheKeys = await window.caches.keys();
      await Promise.all(cacheKeys.map((cacheKey) => window.caches.delete(cacheKey)));
    } catch (error) {
      console.error('Failed to clear caches:', error);
    }
  }, []);

  const checkForUpdates = useCallback(async () => {
    try {
      // In a real scenario, you'd fetch a small version.json from the server
      const response = await fetch('/version.json?t=' + Date.now(), {
        cache: 'no-store'
      });
      
      if (!response.ok || isLocalhost) return;

      const data = await response.json();
      const latestVersion = String(data.version);
      const pendingUpdateVersion = localStorage.getItem(PENDING_UPDATE_VERSION_KEY);

      if (!currentVersion) {
        // Initial setup
        localStorage.setItem(APP_VERSION_KEY, latestVersion);
        localStorage.removeItem(PENDING_UPDATE_VERSION_KEY);
        setCurrentVersion(latestVersion);
      } else if (latestVersion === currentVersion) {
        localStorage.removeItem(PENDING_UPDATE_VERSION_KEY);
        setLatestVersion(null);
        setIsUpdateAvailable(false);
      } else if (latestVersion !== currentVersion) {
        if (pendingUpdateVersion === latestVersion) {
          await clearAppCaches();
          localStorage.setItem(APP_VERSION_KEY, latestVersion);
          localStorage.removeItem(PENDING_UPDATE_VERSION_KEY);
          sessionStorage.removeItem(UPDATE_SHOWN_KEY);
          setCurrentVersion(latestVersion);
          setLatestVersion(null);
          setIsUpdateAvailable(false);
          return;
        }

        // Update detected
        setLatestVersion(latestVersion);
        setIsUpdateAvailable(true);
        localStorage.setItem(PENDING_UPDATE_VERSION_KEY, latestVersion);
      }
    } catch (error) {
      console.error('Update check failed:', error);
    }
  }, [clearAppCaches, currentVersion, isLocalhost]);

  const triggerUpdate = useCallback(async () => {
    const nextVersion = latestVersion ?? currentVersion;

    if (nextVersion) {
      localStorage.setItem(APP_VERSION_KEY, nextVersion);
      localStorage.removeItem(PENDING_UPDATE_VERSION_KEY);
      setCurrentVersion(nextVersion);
    }

    sessionStorage.removeItem(UPDATE_SHOWN_KEY);
    setIsUpdateAvailable(false);
    await clearAppCaches();

    const url = new URL(window.location.href);
    url.searchParams.set('reload', Date.now().toString());
    window.location.replace(url.toString());
  }, [clearAppCaches, currentVersion, latestVersion]);

  const closeUpdatePopup = useCallback(() => {
    setIsUpdateAvailable(false);
    // Don't show again in this session
    sessionStorage.setItem(UPDATE_SHOWN_KEY, 'true');
  }, []);

  // Interval check
  useEffect(() => {
    if (isLocalhost || sessionStorage.getItem(UPDATE_SHOWN_KEY) === 'true') return;

    // Check on mount
    checkForUpdates();

    // Check every 30 minutes thereafter
    const interval = setInterval(checkForUpdates, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [checkForUpdates]);

  return {
    isUpdateAvailable,
    triggerUpdate,
    closeUpdatePopup,
    checkForUpdates,
    currentVersion
  };
};
